import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useBackRoute } from '../hooks/useBackRoute';
import { sanitizeSearchInput, toValidObjectIdString } from '../utils/security';
import {
  fetchIadeKargolari,
  fetchIadeKargoDetay,
  iadeKargoKargoGir,
} from '../services/iadeKargoService';
import IadeKargoDetayModal from '../components/IadeKargoDetayModal';
import KargoFirmasiField from '../components/KargoFirmasiField';
import TableListFooter from '../components/TableListFooter';
import { PAGE_DEFAULT_LIMIT } from '../constants/pagination';
import { normalizeSayfalama } from '../utils/pagination';

const TABS = [
  { id: 'kargo-bekliyor', label: 'Kargo Bekliyor', durum: 'KargoBekliyor' },
  { id: 'kargoda', label: 'Kargoda', durum: 'Kargoda' },
  { id: 'tamamlandi', label: 'Tamamlandı', durum: 'Tamamlandi' },
];

const formatDateTime = (dateValue) => {
  if (!dateValue) return '—';
  try {
    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    if (!date || isNaN(date.getTime())) return '—';
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const TechServiceIadeKargolariPage = () => {
  const goBack = useBackRoute('/');
  const [iadeKargolari, setIadeKargolari] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({ search: '', tab: 'kargo-bekliyor' });
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedIadeKargo, setSelectedIadeKargo] = useState(null);
  const [kargoModal, setKargoModal] = useState({ open: false, ik: null });
  const [kargoForm, setKargoForm] = useState({ kargoFirmasi: '', kargoTakipNo: '' });
  const [actionLoading, setActionLoading] = useState(null);

  const sanitizedSearch = useMemo(
    () => (filters.search && filters.search.trim() ? filters.search.trim() : ''),
    [filters.search]
  );

  const tabConfig = useMemo(() => TABS.find((t) => t.id === filters.tab) || TABS[0], [filters.tab]);

  const loadIadeKargolari = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchIadeKargolari({
        durum: tabConfig.durum,
        arama: sanitizedSearch || undefined,
        sayfa: currentPage,
        limit: PAGE_DEFAULT_LIMIT,
      });
      if (response?.basarili && response?.veri) {
        setIadeKargolari(response.veri.iadeKargolari || []);
        setPaginationMeta(normalizeSayfalama(response.veri.sayfalama));
      }
    } catch {
      setIadeKargolari([]);
    } finally {
      setLoading(false);
    }
  }, [tabConfig.durum, sanitizedSearch, currentPage]);

  useEffect(() => {
    loadIadeKargolari();
  }, [loadIadeKargolari]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const handleViewDetail = async (ik) => {
    const id = toValidObjectIdString(ik?.id ?? ik?._id);
    if (!id) {
      window.showNotification?.('error', 'Geçersiz kayıt');
      return;
    }
    try {
      const response = await fetchIadeKargoDetay(id);
      if (response?.basarili && response?.veri) {
        setSelectedIadeKargo(response.veri);
        setDetailModalOpen(true);
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Detay yüklenemedi'));
    }
  };

  const handleKargoSubmit = async () => {
    if (!kargoModal.ik) return;
    const ikId = toValidObjectIdString(kargoModal.ik?.id ?? kargoModal.ik?._id);
    if (!ikId) {
      window.showNotification?.('error', 'Geçersiz kayıt');
      return;
    }
    setActionLoading(ikId);
    try {
      const response = await iadeKargoKargoGir(ikId, kargoForm);
      if (response?.basarili) {
        window.showNotification?.('success', 'Kargo bilgisi kaydedildi');
        setKargoModal({ open: false, ik: null });
        loadIadeKargolari();
        setDetailModalOpen(false);
      } else {
        window.showNotification?.('error', response?.mesaj || 'Kayıt başarısız');
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Kayıt başarısız'));
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <main className="main-content">
      <div className="content-header">
        <div>
          <button
            type="button"
            className="secondary-btn"
            onClick={goBack}
            style={{ marginBottom: '16px' }}
          >
            <i className="fas fa-arrow-left"></i>
            <span>Geri Dön</span>
          </button>
          <h1>İade Kargoları</h1>
        </div>
      </div>

      <div className="toolbar" style={{ marginBottom: '16px' }}>
        <div className="search-box" style={{ flex: 1, maxWidth: '350px' }}>
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="İade kargo no veya sipariş no ile ara..."
            value={searchInput}
            onChange={(e) => setSearchInput(sanitizeSearchInput(e.target.value, { trimEdges: false }))}
            maxLength={100}
            autoComplete="off"
          />
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`tab-btn ${filters.tab === tab.id ? 'active' : ''}`}
            onClick={() => {
              setFilters((prev) => ({ ...prev, tab: tab.id }));
              setCurrentPage(1);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="card">
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-gray)' }}>
            Yükleniyor...
          </div>
        ) : iadeKargolari.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-gray)' }}>
            Kayıt bulunamadı
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="admins-table">
                <thead>
                  <tr>
                    <th>İade Kargo No</th>
                    <th>Sipariş No</th>
                    <th>Kaynak</th>
                    <th>Tarih</th>
                    <th className="islemler-sutunu">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {iadeKargolari.map((ik, idx) => (
                    <tr key={ik.iadeKargoNumarasi || toValidObjectIdString(ik?.id ?? ik?._id) || `ik-${idx}`}>
                      <td style={{ fontWeight: 500 }}>{ik.iadeKargoNumarasi}</td>
                      <td>{ik.siparisNumarasi}</td>
                      <td>{ik.stoktanIade ? 'Stoktan iade' : (ik.servisKaydiNumarasi || '—')}</td>
                      <td>{formatDateTime(ik.olusturmaTarihi)}</td>
                      <td className="islemler-sutunu">
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'nowrap' }}>
                          <button
                            type="button"
                            className="category-action-btn edit-btn"
                            onClick={() => handleViewDetail(ik)}
                            title="Detay"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          {filters.tab === 'kargo-bekliyor' && (
                            <button
                              type="button"
                              className="category-action-btn edit-btn"
                              onClick={() => {
                                setKargoModal({ open: true, ik });
                                setKargoForm({ kargoFirmasi: '', kargoTakipNo: '' });
                              }}
                              title="Kargo Bilgisi Gir"
                              disabled={actionLoading === toValidObjectIdString(ik?.id ?? ik?._id)}
                            >
                              <i className="fas fa-truck"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TableListFooter
              totalRecords={paginationMeta.total}
              currentPage={currentPage}
              totalPages={paginationMeta.totalPages || 1}
              onPageChange={setCurrentPage}
              loading={loading}
              embedded
            />
          </>
        )}
      </div>

      {detailModalOpen && selectedIadeKargo && (
        <IadeKargoDetayModal
          iadeKargo={selectedIadeKargo}
          onClose={() => setDetailModalOpen(false)}
          onKargoGir={() => {
            setDetailModalOpen(false);
            setKargoModal({
              open: true,
              ik: {
                ...selectedIadeKargo,
                id: selectedIadeKargo.id ?? selectedIadeKargo._id,
              },
            });
            setKargoForm({ kargoFirmasi: '', kargoTakipNo: '' });
          }}
        />
      )}

      {kargoModal.open && kargoModal.ik && (
        <div className="modal-overlay" onClick={() => setKargoModal({ open: false, ik: null })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2>Kargo Bilgisi - {kargoModal.ik.iadeKargoNumarasi}</h2>
              <button type="button" className="modal-close-btn" onClick={() => setKargoModal({ open: false, ik: null })}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '13px', color: 'var(--text-gray)', marginBottom: '12px' }}>
                Kargoya verdiğinizi işaretleyin. Kargo firması ve takip no opsiyoneldir.
              </p>
              <div style={{ marginBottom: '12px' }}>
                <KargoFirmasiField
                  idPrefix="iade-kargo"
                  value={kargoForm.kargoFirmasi}
                  onChange={(ad) => setKargoForm((prev) => ({ ...prev, kargoFirmasi: ad }))}
                  disabled={!!actionLoading}
                  optionalLabel
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Kargo Takip No (opsiyonel)</label>
                <input
                  type="text"
                  value={kargoForm.kargoTakipNo}
                  onChange={(e) => setKargoForm((prev) => ({ ...prev, kargoTakipNo: e.target.value }))}
                  placeholder="Takip numarası"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setKargoModal({ open: false, ik: null })}>
                İptal
              </button>
              <button type="button" className="btn btn-primary" onClick={handleKargoSubmit} disabled={actionLoading}>
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default TechServiceIadeKargolariPage;
